import React, { useEffect, useState } from 'react';
import axios from 'axios';

const GamificationStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get('http://localhost:5001/api/users/gamification/stats', {
                    headers: { 'x-auth-token': token }
                });
                setStats(res.data);
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.errors?.[0]?.msg || 'Failed to load stats');
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="flex min-h-[200px] items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-500" />
            </div>
        );
    }

    if (error) {
        const motivationalMessages = [
            {
                title: "Hãy bắt đầu học nào!",
                subtitle: "Hành trình ngàn dặm bắt đầu từ bước chân đầu tiên",
                emoji: "🚀",
                gradient: "from-blue-500 via-purple-500 to-pink-500"
            },
            {
                title: "Khám phá tri thức mới!",
                subtitle: "Mỗi ngày học một điều mới là mỗi ngày thành công",
                emoji: "📚",
                gradient: "from-green-500 via-teal-500 to-blue-500"
            },
            {
                title: "Sẵn sàng chinh phục!",
                subtitle: "Kiến thức là chìa khóa mở cánh cửa tương lai",
                emoji: "🎯",
                gradient: "from-orange-500 via-red-500 to-pink-500"
            },
            {
                title: "Cùng bắt đầu thôi!",
                subtitle: "Học tập là hành trình không có điểm dừng",
                emoji: "✨",
                gradient: "from-indigo-500 via-purple-500 to-pink-500"
            }
        ];

        const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];

        return (
            <div className="space-y-6">
                {/* Main Motivational Card */}
                <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${randomMessage.gradient} p-8 shadow-2xl`}>
                    <div className="absolute -right-10 -top-10 text-9xl opacity-20">
                        {randomMessage.emoji}
                    </div>
                    <div className="relative z-10 text-center text-white">
                        <div className="mb-4 text-6xl">{randomMessage.emoji}</div>
                        <h2 className="mb-3 text-3xl font-bold drop-shadow-lg">
                            {randomMessage.title}
                        </h2>
                        <p className="mb-6 text-lg font-medium text-white/90">
                            {randomMessage.subtitle}
                        </p>
                        <div className="mx-auto max-w-md rounded-2xl bg-white/20 p-4 backdrop-blur-sm">
                            <p className="text-sm font-medium text-white/95">
                                💡 Hãy tham gia các lớp học và làm bài kiểm tra để theo dõi tiến độ của bạn!
                            </p>
                        </div>
                    </div>
                </div>

                {/* Quick Action Cards */}
                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="group cursor-pointer rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                        <div className="mb-3 text-4xl transition-transform group-hover:scale-110">📖</div>
                        <h3 className="mb-1 font-semibold text-slate-900">Tham gia lớp học</h3>
                        <p className="text-sm text-slate-600">Khám phá các khóa học mới</p>
                    </div>
                    <div className="group cursor-pointer rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 p-5 shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                        <div className="mb-3 text-4xl transition-transform group-hover:scale-110">🎯</div>
                        <h3 className="mb-1 font-semibold text-slate-900">Làm bài kiểm tra</h3>
                        <p className="text-sm text-slate-600">Thử thách bản thân</p>
                    </div>
                    <div className="group cursor-pointer rounded-2xl bg-gradient-to-br from-green-50 to-teal-50 p-5 shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                        <div className="mb-3 text-4xl transition-transform group-hover:scale-110">📅</div>
                        <h3 className="mb-1 font-semibold text-slate-900">Lịch học</h3>
                        <p className="text-sm text-slate-600">Xem lịch học và sự kiện</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats) return null;

    const { stats: progressStats } = stats;

    return (
        <div className="space-y-6">
            {/* Progress Overview */}
            <div className="rounded-3xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-6 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-medium text-indigo-100">Tiến độ học tập</h3>
                        <p className="mt-1 text-4xl font-bold">{progressStats.totalQuizzes}</p>
                        <p className="text-sm text-indigo-100">Bài kiểm tra đã hoàn thành</p>
                    </div>
                    <div className="text-5xl">📊</div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/80 p-5 shadow-lg">
                    <div className="text-3xl">📝</div>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{progressStats.totalQuizzes}</p>
                    <p className="text-sm text-slate-500">Bài kiểm tra</p>
                </div>
                <div className="rounded-2xl bg-white/80 p-5 shadow-lg">
                    <div className="text-3xl">⭐</div>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{progressStats.averageScore}%</p>
                    <p className="text-sm text-slate-500">Điểm trung bình</p>
                </div>
            </div>

            {/* Recent Attempts */}
            {progressStats.recentAttempts && progressStats.recentAttempts.length > 0 && (
                <div className="rounded-3xl bg-white/90 p-6 shadow-xl">
                    <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900">
                        <span>📈</span> Kết quả gần đây
                    </h3>
                    <div className="space-y-3">
                        {progressStats.recentAttempts.map((attempt, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between rounded-xl bg-slate-50 p-4"
                            >
                                <div className="flex-1">
                                    <h4 className="font-medium text-slate-900">{attempt.quizTitle}</h4>
                                    <p className="text-sm text-slate-500">
                                        {new Date(attempt.submittedAt).toLocaleDateString('vi-VN')}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-lg font-bold text-slate-900">
                                        {attempt.score}/{attempt.totalPoints}
                                    </p>
                                    <p className={`text-sm font-semibold ${
                                        attempt.percentage >= 80 ? 'text-green-600' :
                                        attempt.percentage >= 60 ? 'text-yellow-600' :
                                        'text-red-600'
                                    }`}>
                                        {attempt.percentage.toFixed(0)}%
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GamificationStats;
